/**
 * RTK Query slice for the SA per-door v1 repair (LOCKER-343, Deliverable B).
 *
 *   fetch → GET  /proxy/sirsteward-v2?resourceType=saLockerDoorFetchV1
 *   apply → POST /proxy/sirsteward-v2?resourceType=saLockerDoorApplyV1
 *
 * Its own slice rather than an endpoint on `thingsApi`, for two reasons. The
 * mechanical one: `features/things/thingsApi.ts` is being edited concurrently by
 * LOCKER-323's server-driven lockers list, and a new endpoint there is a conflict for
 * nothing. The real one: these two are partner-proxy calls behind a super-admin gate,
 * not Thing reads, and `sstv2EvidenceApi` already established that shape for exactly
 * this kind of SA-only mutating endpoint.
 *
 * ## The fetch is a `mutation`, deliberately, even though it writes nothing
 *
 * A `query` would cache and auto-refetch, and both are wrong here. The value being
 * read is a LIVE partner state that the operator is deciding an irreversible action
 * from — a cached "class A" rendered after the door has already been repaired is
 * exactly how someone applies a stale decision. `mutation` gives an explicit
 * "I am asking now", which is the interaction the drawer actually offers. The backend
 * is what guarantees it writes nothing; a test asserts that.
 */
import { createApi } from "@reduxjs/toolkit/query/react"
import type {
  DoorDivergenceClass,
  DoorRepairAction,
} from "shared/saLockerDoorRepairData"
import { baseQuery } from "../baseQuerySl2"

export type DoorDivergence = {
  class: DoorDivergenceClass | null
  reason: string
  action: DoorRepairAction | null
  irreversible: boolean
}

export type SaLockerDoorFetchResult = {
  data: {
    sstDoorId: string
    doorNumber?: number
    doorLabel?: string
    partnerDoor?: Record<string, unknown>
    partnerSstResId?: string
    /** The partner's v1 reservation, verbatim — the payload v1 could never show. */
    partnerReservation?: Record<string, unknown>
    sl2Reservation?: Record<string, unknown>
    sl2ReservationsOnDoor: Record<string, unknown>[]
    divergence: DoorDivergence
  }
}

export type SaLockerDoorApplyArgs = {
  sstLockerId: string
  sstDoorId: string
  /** Both required and both sent verbatim — the backend re-checks the pairing. */
  divergenceClass: DoorDivergenceClass
  action: DoorRepairAction
}

export type SaLockerDoorApplyResult = {
  data: {
    applied: boolean
    /**
     * `false` means the change landed but its audit record did NOT — live and
     * unrecorded. The panel must not render that as an unqualified success.
     */
    auditWritten?: boolean
    divergenceClass?: DoorDivergenceClass
    action?: DoorRepairAction
    reason?: string
    reservationId?: string
    /** The door re-read AFTER the change, so the drawer can show it clean. */
    after?: SaLockerDoorFetchResult["data"]
  }
}

export const saLockerRepairApi = createApi({
  reducerPath: "saLockerRepairApi",
  baseQuery,
  endpoints: (builder) => ({
    fetchSaLockerDoorV1: builder.mutation<
      SaLockerDoorFetchResult,
      { sstLockerId: string; sstDoorId: string }
    >({
      query: ({ sstLockerId, sstDoorId }) => ({
        url: "proxy/sirsteward-v2",
        method: "GET",
        params: {
          resourceType: "saLockerDoorFetchV1",
          sstLockerId,
          sstDoorId,
        },
      }),
    }),
    applySaLockerDoorRepairV1: builder.mutation<
      SaLockerDoorApplyResult,
      SaLockerDoorApplyArgs
    >({
      query: (arg) => ({
        url: "proxy/sirsteward-v2",
        method: "POST",
        params: { resourceType: "saLockerDoorApplyV1" },
        // Every field forwarded explicitly, nothing defaulted here. The backend
        // rejects a body missing any of the four rather than assuming one — a
        // class-B apply writes an irreversible CLOSE, so "the caller omitted a
        // field" must never resolve to "do something".
        body: {
          sstLockerId: arg.sstLockerId,
          sstDoorId: arg.sstDoorId,
          divergenceClass: arg.divergenceClass,
          action: arg.action,
        },
      }),
    }),
  }),
})

export const {
  useFetchSaLockerDoorV1Mutation,
  useApplySaLockerDoorRepairV1Mutation,
} = saLockerRepairApi
